import React, { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Compact inline image attachment component
 * Sits at the bottom of a text area - space saving design
 */
export default function InlineImageAttach({
  testIdPrefix = "img",
  urls,
  setUrls,
  maxItems = 3,
  onDragActive,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const removeUrl = (u) => setUrls((prev) => prev.filter((x) => x !== u));

  const uploadFile = async (file) => {
    if (!file) return;
    if (urls.length >= maxItems) return;
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/uploads/images", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.url) {
        const full = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
        setUrls((prev) => [...prev, full].slice(0, maxItems));
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Upload failed";
      setError(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    onDragActive?.(false);
    if (urls.length >= maxItems) return;
    const file = e.dataTransfer.files?.[0];
    await uploadFile(file);
  };

  return (
    <div
      data-testid={`${testIdPrefix}-container`}
      onDragEnter={(e) => {
        e.preventDefault();
        onDragActive?.(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        onDragActive?.(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        onDragActive?.(false);
      }}
      onDrop={handleDrop}
    >
      {/* Thumbnails row */}
      {urls.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {urls.map((u, idx) => (
            <div
              key={u}
              data-testid={`${testIdPrefix}-thumb-${idx}`}
              className="relative h-14 w-14 overflow-hidden rounded-lg border border-slate-700 bg-slate-800"
            >
              <img alt="" src={u} className="h-full w-full object-cover" />
              <button
                data-testid={`${testIdPrefix}-remove-${idx}`}
                type="button"
                onClick={() => removeUrl(u)}
                className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white shadow-sm hover:bg-slate-900"
                title="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Compact attach bar */}
      <div className="flex items-center gap-2">
        <button
          data-testid={`${testIdPrefix}-attach-btn`}
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || urls.length >= maxItems}
          className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 bg-slate-800/50 px-3 py-1.5 text-xs font-medium text-slate-400 hover:bg-slate-700 hover:text-slate-200 disabled:opacity-50"
        >
          {uploading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Uploading…</span>
            </>
          ) : (
            <>
              <ImagePlus className="h-3.5 w-3.5" />
              <span>Add image</span>
            </>
          )}
        </button>

        <span className="text-xs text-slate-500">
          {urls.length}/{maxItems} • drag & drop supported
        </span>

        <input
          data-testid={`${testIdPrefix}-file-input`}
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            await uploadFile(file);
          }}
        />
      </div>

      {error && (
        <div data-testid={`${testIdPrefix}-error`} className="mt-1 text-xs font-medium text-red-400">
          {error}
        </div>
      )}
    </div>
  );
}
